import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Link as RouterLink } from "react-router-dom";
import { useMarketplaceListings } from "../hooks/useMarketplaceApi";
import { MarketplaceListing } from "../marketplaceStorage";

type ListingFilterType = "ALL" | "AFH_SALE" | "ELDER_GIFT";
type GiftCategoryFilter = "ALL" | string;
const DEFAULT_GIFT_CATEGORIES = [
  "Mobility & Safety",
  "Comfort & Bedding",
  "Memory Care Activities",
  "Daily Living Aids",
  "Wellness & Therapy",
  "Adaptive Clothing",
  "Entertainment & Games",
  "Personal Care",
] as const;

const isDefaultGiftCategory = (value?: string | null) =>
  DEFAULT_GIFT_CATEGORIES.includes(String(value || "").trim() as any);

const normalizeGiftCategoryGroup = (value?: string | null) =>
  isDefaultGiftCategory(value) ? String(value || "").trim() : "Other";

const displayGiftCategory = (value?: string | null) => {
  const v = String(value || "").trim();
  if (!v) return "Other";
  if (isDefaultGiftCategory(v)) return v;
  return `Other - ${v}`;
};

function money(n: number) {
  return `$${Number(n || 0).toLocaleString()}`;
}

export default function MarketplacePage() {
  const [search, setSearch] = React.useState("");
  const [kind, setKind] = React.useState<ListingFilterType>("ALL");
  const [giftCategoryFilter, setGiftCategoryFilter] = React.useState<GiftCategoryFilter>("ALL");
  const [maxPrice, setMaxPrice] = React.useState("");
  const [contactTarget, setContactTarget] = React.useState<MarketplaceListing | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [imageIndexByListing, setImageIndexByListing] = React.useState<Record<string, number>>({});
  const { listings, loading, error, refetch } =
    useMarketplaceListings({
      q: search,
      type: kind,
      maxPrice: maxPrice ? Number(maxPrice) : null,
      status: "ACTIVE",
    });

  const giftCategoryOptions = React.useMemo(
    () => [...DEFAULT_GIFT_CATEGORIES, "Other"] as string[],
    []
  );

  const filteredListings = React.useMemo(() => {
    if (giftCategoryFilter === "ALL") return listings;
    return (listings || []).filter(
      (l: MarketplaceListing) =>
        l.type === "ELDER_GIFT" &&
        normalizeGiftCategoryGroup(l.giftCategory) === giftCategoryFilter
    );
  }, [giftCategoryFilter, listings]);

  const afhListings = React.useMemo(
    () => (filteredListings || []).filter((l: MarketplaceListing) => l.type === "AFH_SALE"),
    [filteredListings]
  );

  const giftListings = React.useMemo(
    () => (filteredListings || []).filter((l: MarketplaceListing) => l.type === "ELDER_GIFT"),
    [filteredListings]
  );

  const giftListingsByCategory = React.useMemo(() => {
    const byCategory = new Map<string, MarketplaceListing[]>();
    for (const listing of giftListings) {
      const category = normalizeGiftCategoryGroup(listing.giftCategory);
      const existing = byCategory.get(category) || [];
      existing.push(listing);
      byCategory.set(category, existing);
    }
    return (["Mobility & Safety", "Comfort & Bedding", "Memory Care Activities", "Daily Living Aids", "Wellness & Therapy", "Adaptive Clothing", "Entertainment & Games", "Personal Care", "Other"] as const)
      .map((cat) => [cat, byCategory.get(cat) || []] as [string, MarketplaceListing[]])
      .filter(([, items]) => items.length > 0);
  }, [giftListings]);

  const getListingImages = React.useCallback((listing: MarketplaceListing) => {
    const images = Array.isArray(listing.imageUrls)
      ? listing.imageUrls.filter(Boolean)
      : [];
    if (images.length) return images;
    return listing.imageUrl ? [listing.imageUrl] : [];
  }, []);

  const shiftListingImage = (listingId: string, total: number, delta: number) => {
    if (total <= 1) return;
    setImageIndexByListing((prev) => {
      const current = prev[listingId] || 0;
      const next = (current + delta + total) % total;
      return { ...prev, [listingId]: next };
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 1.5, sm: 2.5 }, py: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Marketplace
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Buy and sell AFH businesses, plus gifts for residents and supplies.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/biz/marketplace?v=care"
          >
            My marketplace
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/care/marketplace/new?type=AFH_SALE"
          >
            Sell AFH
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            component={RouterLink}
            to="/care/marketplace/new"
          >
            Post Listing
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              placeholder="Search title, city, business, or details"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
            <TextField
              select
              size="small"
              label="Type"
              value={kind}
              onChange={(e) => setKind(e.target.value as ListingFilterType)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="ALL">All listings</MenuItem>
              <MenuItem value="AFH_SALE">AFH for sale</MenuItem>
              <MenuItem value="ELDER_GIFT">Gifts for residents</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Max price"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <TextField
              select
              size="small"
              label="Gift category"
              value={giftCategoryFilter}
              onChange={(e) => setGiftCategoryFilter(e.target.value)}
              sx={{ minWidth: 200 }}
              disabled={kind === "AFH_SALE"}
            >
              <MenuItem value="ALL">All gift categories</MenuItem>
                {giftCategoryOptions.map((c: string) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {!!error && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Unable to load marketplace data from backend.
        </Alert>
      )}

      {!loading && filteredListings.length === 0 ? (
        <Alert severity="info">No listings matched your filters.</Alert>
      ) : (
        <Stack spacing={2}>
          {afhListings.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                AFH For Sale
              </Typography>
              <Grid container spacing={1.5}>
                {afhListings.map((i: MarketplaceListing) => (
                  <MarketplaceListingCard
                    key={i.id}
                    listing={i}
                    imageIndexByListing={imageIndexByListing}
                    getListingImages={getListingImages}
                    shiftListingImage={shiftListingImage}
                    onContact={(listing) => {
                      setCopied(false);
                      setContactTarget(listing);
                    }}
                  />
                ))}
              </Grid>
            </Box>
          )}

          {giftListingsByCategory.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Gifts For Residents
              </Typography>
              <Stack spacing={1.5}>
                {giftListingsByCategory.map(([category, categoryListings]) => (
                  <Box key={category}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                      {category}
                    </Typography>
                    <Grid container spacing={1.5}>
                      {categoryListings.map((i: MarketplaceListing) => (
                        <MarketplaceListingCard
                          key={i.id}
                          listing={i}
                          imageIndexByListing={imageIndexByListing}
                          getListingImages={getListingImages}
                          shiftListingImage={shiftListingImage}
                          onContact={(listing) => {
                            setCopied(false);
                            setContactTarget(listing);
                          }}
                        />
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      <Dialog open={!!contactTarget} onClose={() => setContactTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Contact Seller</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {contactTarget?.title}
            </Typography>
            <Typography variant="body2">
              Email: {contactTarget?.contactEmail || "-"}
            </Typography>
            {!!contactTarget?.contactPhone && (
              <Typography variant="body2">Phone: {contactTarget.contactPhone}</Typography>
            )}
            {copied && (
              <Alert severity="success" sx={{ py: 0 }}>
                Email copied
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            size="small"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={async () => {
              if (!contactTarget?.contactEmail) return;
              try {
                await navigator.clipboard.writeText(contactTarget.contactEmail);
                setCopied(true);
              } catch {
                setCopied(false);
              }
            }}
          >
            Copy Email
          </Button>
          {!!contactTarget?.contactPhone && (
            <Button
              size="small"
              component="a"
              href={`tel:${contactTarget.contactPhone}`}
              startIcon={<PhoneInTalkRoundedIcon />}
            >
              Call
            </Button>
          )}
          {!!contactTarget?.contactEmail && (
            <Button
              size="small"
              variant="contained"
              component="a"
              href={`mailto:${contactTarget.contactEmail}?subject=${encodeURIComponent(
                `Marketplace inquiry: ${contactTarget.title}`
              )}`}
            >
              Email
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function MarketplaceListingCard({
  listing,
  imageIndexByListing,
  getListingImages,
  shiftListingImage,
  onContact,
}: {
  listing: MarketplaceListing;
  imageIndexByListing: Record<string, number>;
  getListingImages: (listing: MarketplaceListing) => string[];
  shiftListingImage: (listingId: string, total: number, delta: number) => void;
  onContact: (listing: MarketplaceListing) => void;
}) {
  const images = getListingImages(listing);
  const currentIndex = Math.min(
    imageIndexByListing[listing.id] || 0,
    Math.max(images.length - 1, 0)
  );

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Card variant="outlined" sx={{ height: "100%" }}>
        {!!images.length && (
          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="img"
              image={images[currentIndex]}
              alt={listing.title}
              sx={{ height: 170, objectFit: "cover" }}
            />
            {images.length > 1 && (
              <>
                <IconButton
                  size="small"
                  onClick={() => shiftListingImage(listing.id, images.length, -1)}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 6,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                  }}
                >
                  <ChevronLeftRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => shiftListingImage(listing.id, images.length, 1)}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 6,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
                  }}
                >
                  <ChevronRightRoundedIcon fontSize="small" />
                </IconButton>
                <Chip
                  size="small"
                  label={`${currentIndex + 1}/${images.length}`}
                  sx={{
                    position: "absolute",
                    right: 8,
                    bottom: 8,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                  }}
                />
              </>
            )}
          </Box>
        )}
        <CardContent sx={{ p: 1.5 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {listing.title}
              </Typography>
              <Chip
                size="small"
                color={listing.type === "AFH_SALE" ? "primary" : "default"}
                label={listing.type === "AFH_SALE" ? "AFH Sale" : "Gift"}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {listing.description}
            </Typography>

            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Chip size="small" label={money(listing.price)} />
              <Chip size="small" label={`${listing.city}, ${listing.state}`} />
              {listing.ownerBusinessName && <Chip size="small" label={listing.ownerBusinessName} />}
              {listing.type === "AFH_SALE" && listing.beds != null && (
                <Chip size="small" label={`${listing.beds} beds`} />
              )}
              {listing.type === "AFH_SALE" && listing.occupancy != null && (
                <Chip size="small" label={`${listing.occupancy} occupied`} />
              )}
              {listing.type === "ELDER_GIFT" && listing.giftCategory && (
                <Chip size="small" label={displayGiftCategory(listing.giftCategory)} />
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                onClick={() => onContact(listing)}
                endIcon={<OpenInNewRoundedIcon />}
              >
                Contact
              </Button>
              <Chip
                size="small"
                color={listing.status === "ACTIVE" ? "success" : listing.status === "SOLD" ? "warning" : "default"}
                label={listing.status}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
